import { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Tooltip, IconButton, Icon } from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import { useLocation } from 'react-router-dom';

import Loader from '../../common/loader';
import NotFound from '../../common/not-found';
import ViewTitle from '../../common/view-title';
import SupervisorUpdate from '../components/supervisor-update';

import AuthContext from '../../context';
import { get } from '../../utils/api-services';
import helpers from '../../utils/helpers';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        paddingBottom: 20
    },
    tableContainer: {
        width: '100%',
        '& .deactivated': {
            backgroundColor: '#F8E0E0'
        }
    },
    employeesToPrint: {
        display: 'none'
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    print: {
        marginRight: 15,
        backgroundColor: '#0d6efd',
        borderColor: '#0d6efd',
        '&:hover': {
            backgroundColor: '#0d6efd',
            borderColor: '#0d6efd',
            boxShadow: 'none'
        }
    }
}));

const Supervisor = () => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const { pathname } = useLocation();
    const [updateModal, setUpdateModal] = useState({ open: false, employee: {} });
    const [state, setState] = useState({
        loading: true,
        data: [],
        without: [],
        employees: [],
        permissions: {}
    });

    useEffect(() => {
        window.document.title = 'Supervisor';

        validatePermissions();
    }, []);

    const validatePermissions = () => {
        const permissions = helpers.getPermissions(pathname);
        setState(prev => ({ ...prev, permissions }));

        (permissions.read_attr) ? makeRequest() : setState(prev => ({ ...prev, loading: false }));
    }

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/employees/supervisor');
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                data: response.data,
                without: response.without,
                employees: response.employees
            }));
        } else {
            console.log('[Supervisor] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut)
        }
        showLoader(false);
    }

    const valueGetter = ({ value }) => value ? '✓' : '✗';

    const getName = ({ value }) => value?.name ?? 'N/A';

    const renderCell = ({ row }) => {
        return (
            <Tooltip title='Update supervisor' placement='top'>
                <IconButton onClick={() => setUpdateModal({ open: true, employee: row })}>
                    <Icon>{'supervisor_account'}</Icon>
                </IconButton>
            </Tooltip>
        );
    }

    const columns = [
        { field: 'code', headerName: 'Code', flex: 0.8, minWidth: 80 },
        { field: 'name', headerName: 'Name', flex: 1.7, minWidth: 170 },
        { field: 'base_rate', headerName: 'Base rate', flex: 0.8, minWidth: 80 },
        { field: 'indicator', headerName: 'Indicator', flex: 0.8, minWidth: 80 },
        { field: 'sede', headerName: 'Sede', flex: 1, minWidth: 100, valueGetter: getName },
        { field: 'is_supplier', headerName: 'Supplier', flex: 0.8, minWidth: 80, valueGetter },
        { field: 'payment', headerName: 'Payment', flex: 0.8, minWidth: 80, valueGetter },
        { field: 'regular_overtime', headerName: 'Regular OT', flex: 0.8, minWidth: 80, valueGetter },
        { field: 'bonus_payment', headerName: 'Bonus payment', flex: 0.8, minWidth: 80, valueGetter },
        { field: 'biweekly_payment', headerName: 'Biweekly payment', flex: 0.8, minWidth: 80, valueGetter },
        { field: 'cc1', headerName: 'CC1', flex: 0.8, minWidth: 80, valueGetter: getName },
        { field: 'cc2', headerName: 'CC2', flex: 0.8, minWidth: 80, valueGetter: getName },
        { field: 'cc3', headerName: 'CC3', flex: 0.8, minWidth: 80, valueGetter: getName },
        { field: 'actions', headerName: 'Actions', flex: 1, minWidth: 100, renderCell }
    ];

    if (state.loading) return <Loader />;
    if (!state.permissions.read_attr) return <NotFound />;

    return (
        <div className={classes.root}>
            <ViewTitle title='Supervisor (employees)' />

            {
                state.data.map(employee =>
                    <div key={employee.idemployee} className={classes.tableContainer}>
                        <h2>{`${employee?.code} - ${employee?.name}`}</h2>
                        <DataGrid
                            autoHeight
                            pageSize={45}
                            density='compact'
                            columns={columns}
                            rows={employee?.supervised ?? []}
                            getRowId={row => row.idemployee}
                            disableSelectionOnClick
                            getRowClassName={({ row }) => row.active ? '' : 'deactivated'}
                        />
                    </div>)
            }

            <div className={classes.tableContainer}>
                <h3>{'Employees w/o supervisor'}</h3>
                <DataGrid
                    autoHeight
                    pageSize={45}
                    density='compact'
                    columns={columns}
                    rows={state.without}
                    getRowId={row => row.idemployee}
                    disableSelectionOnClick
                    getRowClassName={({ row }) => row.active ? '' : 'deactivated'}
                />
            </div>

            <SupervisorUpdate
                open={updateModal.open}
                employee={updateModal.employee}
                employees={state.employees}
                onClose={() => setUpdateModal({ open: false, employee: {} })}
            />
        </div>
    );
}

export default Supervisor;